import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import {
  ArrowLeft, Upload, Trash2, Plus, Phone, Mail, FileText, Download,
  AlertTriangle, Calendar, User, Building2, ExternalLink, Pencil, CheckCircle2,
} from 'lucide-react';
import PaymentStructureEditor from '@/components/PaymentStructureEditor';
import { AddContractButton, ContractNotes, CommentsBox } from '@/components/ClientDossierHelpers';
import CustomerIntelligenceDashboard from '@/components/CustomerIntelligenceDashboard';

const fC = (v) => {
  const n = Number(v) || 0;
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2).replace(/\.?0+$/, '')}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1).replace(/\.?0+$/, '')}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
};

const HEALTH_STYLE = {
  green: { bg: '#ECFDF5', text: '#065F46', accent: '#2D8B5E', label: 'Healthy' },
  amber: { bg: '#FEF3C7', text: '#92400E', accent: '#BA7517', label: 'Watch' },
  red:   { bg: '#FEF2F2', text: '#991B1B', accent: '#D4726A', label: 'At Risk' },
};

const PAYMENT_TERMS = ['Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90',
  'Advance 50%', 'Advance 100%', 'Milestone-based', 'PDC', 'On receipt', 'Other'];
const BILLING_CYCLES = ['One-time', 'Monthly', 'Quarterly', 'Semi-annual', 'Annual', 'Milestone'];

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'contracts', label: 'Contracts' },
  { id: 'documents', label: 'Documents' },
  { id: 'meetings', label: 'Meeting Notes' },
  { id: 'payments', label: 'Payments' },
  { id: 'history', label: 'Revenue History' },
  { id: 'intelligence', label: 'Intelligence' },
];

// Clients that have Customer Intelligence wired up. Other clients show
// "Intelligence not enabled" instead of an empty dashboard.
const INTELLIGENCE_CLIENTS = new Set(['jlr', 'aabe', 'dreamfolks', 'redberyl', 'xiaomi']);

export default function ClientDossierPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('overview');

  const refresh = async () => {
    try {
      const r = await api.get(`/clients-registry/${clientId}/dossier`);
      setDossier(r.data);
    } catch (e) {
      setDossier(null);
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, [clientId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!dossier) {
    return (
      <div className="text-center py-16">
        <p className="text-sm" style={{ color: '#9CA3AF' }}>Client not found</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-sm underline" style={{ color: '#2A7B88' }}>Go back</button>
      </div>
    );
  }

  const c = dossier.client;
  const health = HEALTH_STYLE[c.satisfaction_indicator] || HEALTH_STYLE.green;

  return (
    <div className="max-w-6xl mx-auto space-y-5 px-2 sm:px-4" data-testid="client-dossier-page">
      {/* Breadcrumb */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-xs hover:text-[#2A7B88] transition-colors" style={{ color: '#9CA3AF' }} data-testid="back-btn">
        <ArrowLeft size={14} /> Back
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 pb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-bold" style={{ color: '#1B2A4A' }} data-testid="client-title">{c.client_name}</h1>
            {c.at_risk && (
              <span className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#FEF2F2', color: '#991B1B' }} data-testid="at-risk-badge">
                <AlertTriangle size={11} /> At Risk
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs" style={{ color: '#6B7280' }}>
            {c.industry && <span>{c.industry}</span>}
            {c.company_size && <><span>·</span><span>{c.company_size}</span></>}
            {c.address && <><span>·</span><span>{c.address}</span></>}
            {c.relationship_owner && <><span>·</span><span>Owner: <strong>{c.relationship_owner}</strong></span></>}
          </div>
          {c.at_risk && c.at_risk_reason && (
            <p className="text-xs mt-1.5 italic" style={{ color: '#991B1B' }} data-testid="at-risk-reason">⚠ {c.at_risk_reason}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs px-3 py-1 rounded-full font-bold" style={{ backgroundColor: health.bg, color: health.text }} data-testid="health-pill">
            {health.label}
          </span>
        </div>
      </div>

      {/* KPI row — Annual Value auto-sums the payment schedule so the
          headline follows the rows exactly. Falls back to registry value
          if no schedule rows exist, or shows MG+overages for variable. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        {(() => {
          const av = computeDynamicAV(dossier);
          let label = 'Annual Value';
          let subtitle = null;
          let hint = null;
          if (av.isVariable) {
            label = 'Billed to-date (MG + overages)';
            subtitle = `Floor ${fC(av.floor)} · ${av.overagesCount} overage${av.overagesCount === 1 ? '' : 's'}`;
            hint = "Auto-computed: MG floor + any 'overage' rows logged. To change the floor → Contracts tab → Edit Terms. To add overage billing → Payments tab → 'Add variable billing'.";
          } else if (av.source === 'schedule') {
            subtitle = `Sum of ${av.rowCount} payment row${av.rowCount === 1 ? '' : 's'}`;
            hint = "Auto-computed. To change this figure → go to Payments tab below. Edit individual rows (pencil), delete rows (trash), or add new rows (+). Every change reflects here instantly.";
          } else {
            hint = "No payment schedule yet. Add a contract under the Contracts tab — payment rows auto-generate based on its billing structure.";
          }
          return (
            <KpiCard
              label={label}
              value={fC(av.totalValue)}
              subtitle={subtitle}
              accent="#378ADD"
              hint={hint}
              onHintAction={() => setSection('payments')}
              hintActionLabel="Go to Payments →"
            />
          );
        })()}
        <KpiCard label="Lifetime Realised" value={fC(dossier.total_realised_lifetime)} accent="#2D8B5E" />
        <KpiCard label="Active Contracts" value={(dossier.contracts || []).filter(x => x.renewal_status !== 'archived' && x.renewal_status !== 'terminated').length} accent="#7C3AED" raw />
        <KpiCard label="Last Interaction" value={c.last_interaction_date || c.last_meeting_date || '—'} accent="#D97706" raw />
      </div>

      {/* Section nav */}
      <div className="border-b overflow-x-auto" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex gap-1.5 pb-2" style={{ scrollbarWidth: 'none' }}>
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => setSection(s.id)}
              className={`whitespace-nowrap px-4 py-2 text-sm rounded-full transition-all shrink-0 ${section === s.id ? 'font-semibold text-white shadow-sm' : 'font-medium text-[#6B7280] hover:bg-[#F7F5F2]'}`}
              style={section === s.id ? { backgroundColor: '#2A7B88' } : {}}
              data-testid={`dossier-section-${s.id}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {section === 'overview' && <OverviewSection dossier={dossier} onRefresh={refresh} />}
      {section === 'contacts' && <ContactsSection dossier={dossier} onRefresh={refresh} />}
      {section === 'contracts' && <ContractsSection dossier={dossier} onRefresh={refresh} />}
      {section === 'documents' && <DocumentsSection dossier={dossier} onRefresh={refresh} />}
      {section === 'meetings' && <MeetingsSection dossier={dossier} onRefresh={refresh} />}
      {section === 'payments' && <PaymentsSection dossier={dossier} onRefresh={refresh} />}
      {section === 'history' && <HistorySection dossier={dossier} />}
    </div>
  );
}

function KpiCard({ label, value, accent, raw, subtitle, hint, onHintAction, hintActionLabel }) {
  const [showHint, setShowHint] = useState(false);
  return (
    <div className="bg-white rounded-xl p-3 border relative" style={{ borderColor: '#E5E1DC' }}>
      <div className="flex items-start justify-between">
        <div className="w-6 h-[3px] rounded-sm mb-2" style={{ backgroundColor: accent }} />
        {hint && (
          <button
            type="button"
            onClick={() => setShowHint((s) => !s)}
            className="text-[10px] w-4 h-4 rounded-full flex items-center justify-center hover:bg-slate-100"
            style={{ color: '#9CA3AF', border: '1px solid #E5E1DC' }}
            title="What is this?"
            data-testid={`kpi-info-${label.replace(/\s+/g, '-').toLowerCase()}`}
          >
            ⓘ
          </button>
        )}
      </div>
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#9CA3AF' }}>{label}</p>
      <p className={`${raw ? 'text-sm' : 'text-xl'} font-bold mt-1`} style={{ color: '#1B2A4A' }}>{value}</p>
      {subtitle && <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>{subtitle}</p>}
      {showHint && hint && (
        <div
          className="absolute top-full left-0 right-0 mt-1 z-10 bg-white border rounded-lg shadow-lg p-3 text-[11px]"
          style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
        >
          <p className="mb-2 leading-relaxed">{hint}</p>
          {onHintAction && hintActionLabel && (
            <button
              onClick={() => { setShowHint(false); onHintAction(); }}
              className="font-semibold text-[11px]"
              style={{ color: '#2A7B88' }}
            >
              {hintActionLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Dynamic "Annual Value" — three cases:
//   1. Variable contract (Xiaomi MG + overages): floor + ad-hoc overage rows.
//   2. Schedule-driven (RedBeryl monthly retainer etc.): sum ALL payment_schedule
//      rows the user has entered — so the KPI follows manual edits exactly.
//   3. No schedule yet: fall back to clients_registry.annual_value.
function computeDynamicAV(dossier) {
  const variable = (dossier.contracts || []).find(
    (c) => c.payment_structure === 'fixed_plus_variable' && c.renewal_status === 'active'
  );
  if (variable) {
    const floor = Number(variable.annual_value || variable.contract_value || 0);
    const sched = (variable.payment_schedule || []);
    const overages = sched.filter((r) => r.kind === 'overage');
    const overageSum = overages.reduce((a, r) => a + Number(r.amount || 0), 0);
    return {
      isVariable: true,
      totalValue: floor + overageSum,
      floor,
      overagesCount: overages.length,
      source: 'variable',
    };
  }
  // Sum every row in every contract's payment_schedule
  const schedSum = (dossier.schedules || []).reduce(
    (a, r) => a + Number(r.amount || 0), 0
  );
  if (schedSum > 0) {
    return {
      isVariable: false,
      totalValue: schedSum,
      floor: 0,
      overagesCount: 0,
      source: 'schedule',
      rowCount: (dossier.schedules || []).length,
    };
  }
  return {
    isVariable: false,
    totalValue: dossier.client?.annual_value || 0,
    floor: 0,
    overagesCount: 0,
    source: 'registry',
  };
}

// ============================================================
// OVERVIEW — inline-edit of core client fields, at-risk flag, notes
// ============================================================
function OverviewSection({ dossier, onRefresh }) {
  const c = dossier.client;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  const startEdit = () => {
    setForm({
      industry: c.industry || '', company_size: c.company_size || '',
      address: c.address || '', website: c.website || '',
      relationship_owner: c.relationship_owner || '',
      satisfaction_indicator: c.satisfaction_indicator || 'green',
      nps_score: c.nps_score || '',
      at_risk: !!c.at_risk, at_risk_reason: c.at_risk_reason || '',
      notes: c.notes || '',
      how_they_found_us: c.how_they_found_us || '',
      why_they_stay: c.why_they_stay || '',
    });
    setEditing(true);
  };

  const save = async () => {
    await api.put(`/clients-registry/${c.id}`, form);
    setEditing(false);
    onRefresh();
  };

  return (
    <div className="space-y-3" data-testid="overview-section">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Company Profile</p>
        {!editing ? (
          <button onClick={startEdit} className="flex items-center gap-1 text-xs" style={{ color: '#2A7B88' }} data-testid="edit-overview-btn"><Pencil size={12} /> Edit</button>
        ) : (
          <div className="flex gap-2">
            <button onClick={save} className="px-3 py-1 text-xs font-medium text-white rounded-md" style={{ backgroundColor: '#2A7B88' }} data-testid="save-overview-btn">Save</button>
            <button onClick={() => setEditing(false)} className="px-3 py-1 text-xs rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border p-4 grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ borderColor: '#E5E1DC' }}>
        <Field label="Industry" value={c.industry} editing={editing} onChange={v => setForm(f => ({ ...f, industry: v }))} current={form.industry} />
        <Field label="Company Size" value={c.company_size} editing={editing} onChange={v => setForm(f => ({ ...f, company_size: v }))} current={form.company_size} placeholder="Mid-size (100-500)" />
        <Field label="Address" value={c.address} editing={editing} onChange={v => setForm(f => ({ ...f, address: v }))} current={form.address} wide />
        <Field label="Website" value={c.website} editing={editing} onChange={v => setForm(f => ({ ...f, website: v }))} current={form.website} />
        <Field label="Relationship Owner" value={c.relationship_owner} editing={editing} onChange={v => setForm(f => ({ ...f, relationship_owner: v }))} current={form.relationship_owner} />
        <Field label="NPS Score (1-10)" value={c.nps_score} editing={editing} onChange={v => setForm(f => ({ ...f, nps_score: v }))} current={form.nps_score} type="number" />
        {editing ? (
          <div>
            <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Relationship Health</label>
            <select value={form.satisfaction_indicator} onChange={e => setForm(f => ({ ...f, satisfaction_indicator: e.target.value }))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="health-select">
              <option value="green">Green — Healthy</option>
              <option value="amber">Amber — Watch</option>
              <option value="red">Red — At Risk</option>
            </select>
          </div>
        ) : (
          <Field label="Relationship Health" value={HEALTH_STYLE[c.satisfaction_indicator]?.label || 'Healthy'} editing={false} />
        )}
        <Field label="How They Found Us" value={c.how_they_found_us} editing={editing} onChange={v => setForm(f => ({ ...f, how_they_found_us: v }))} current={form.how_they_found_us} />
        <Field label="Why They Stay" value={c.why_they_stay} editing={editing} onChange={v => setForm(f => ({ ...f, why_they_stay: v }))} current={form.why_they_stay} wide />

        {/* At Risk flag */}
        <div className="sm:col-span-2 pt-3 border-t" style={{ borderColor: '#F3F4F6' }}>
          {editing ? (
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm" style={{ color: '#1B2A4A' }}>
                <input type="checkbox" checked={!!form.at_risk} onChange={e => setForm(f => ({ ...f, at_risk: e.target.checked }))} data-testid="at-risk-checkbox" />
                <AlertTriangle size={13} style={{ color: '#D4726A' }} />
                <span className="font-medium">Flag as At Risk</span>
              </label>
              {form.at_risk && (
                <input value={form.at_risk_reason} onChange={e => setForm(f => ({ ...f, at_risk_reason: e.target.value }))} placeholder="Reason (e.g. Budget cut, SPOC change, Competitor pitching)" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#D4726A' }} data-testid="at-risk-reason-input" />
              )}
            </div>
          ) : c.at_risk ? (
            <div className="flex items-start gap-2 p-3 rounded-md" style={{ backgroundColor: '#FEF2F2' }}>
              <AlertTriangle size={16} style={{ color: '#991B1B' }} className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold" style={{ color: '#991B1B' }}>At Risk</p>
                <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>{c.at_risk_reason || 'No reason specified'}</p>
              </div>
            </div>
          ) : null}
        </div>

        {/* Long-form notes */}
        <div className="sm:col-span-2">
          <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>About the Client / Notes</label>
          {editing ? (
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={4} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC' }} data-testid="notes-textarea" />
          ) : (
            <p className="text-sm mt-0.5 whitespace-pre-wrap" style={{ color: c.notes ? '#1B2A4A' : '#D4D4D8' }}>{c.notes || '—'}</p>
          )}
        </div>
      </div>

      {/* Append-only CEO comments. Newest on top. */}
      <CommentsBox clientId={c.id} comments={c.ceo_comments || []} tab="overview" onAdded={onRefresh} />
    </div>
  );
}

function Field({ label, value, editing, onChange, current, type = 'text', placeholder, wide }) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <label className="text-[10px] font-medium uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{label}</label>
      {editing && onChange ? (
        <input type={type} value={current || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
      ) : (
        <p className="text-sm mt-0.5" style={{ color: value ? '#1B2A4A' : '#D4D4D8' }}>{value || '—'}</p>
      )}
    </div>
  );
}

// ============================================================
// CONTACTS
// ============================================================
function ContactsSection({ dossier, onRefresh }) {
  const c = dossier.client;
  const contacts = c.key_contacts || [];
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', designation: '', phone: '', email: '', notes: '', is_primary: false });

  const reset = () => { setForm({ name: '', designation: '', phone: '', email: '', notes: '', is_primary: false }); setShowForm(false); };
  const save = async (e) => {
    e.preventDefault();
    await api.post(`/clients-registry/${c.id}/contacts`, form);
    reset();
    onRefresh();
  };
  const del = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    await api.delete(`/clients-registry/${c.id}/contacts/${contactId}`);
    onRefresh();
  };

  return (
    <div className="space-y-3" data-testid="contacts-section">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Key Contacts ({contacts.length})</p>
        <button onClick={() => setShowForm(s => !s)} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-contact-btn">
          <Plus size={12} /> Add Contact
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="contact-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Name *" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="contact-name-input" />
            <input value={form.designation} onChange={e => setForm(f => ({ ...f, designation: e.target.value }))} placeholder="Designation (e.g. HR Head, Finance Director)" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="Phone" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
          </div>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Notes (e.g. Prefers WhatsApp, decision-maker)" className="w-full px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC' }} />
          <label className="flex items-center gap-2 text-xs" style={{ color: '#6B7280' }}>
            <input type="checkbox" checked={form.is_primary} onChange={e => setForm(f => ({ ...f, is_primary: e.target.checked }))} />
            Primary SPOC
          </label>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="contact-submit">Save</button>
            <button type="button" onClick={reset} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </form>
      )}

      {contacts.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No contacts yet. Add the first SPOC.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {contacts.map((k, i) => (
            <div key={k.id || i} className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }} data-testid={`contact-card-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{k.name}</p>
                    {k.is_primary && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#ECFDF5', color: '#065F46' }}>PRIMARY</span>}
                  </div>
                  <p className="text-[11px] mt-0.5" style={{ color: '#6B7280' }}>{k.designation || k.role || '—'}</p>
                </div>
                {k.id && (
                  <button onClick={() => del(k.id)} className="p-1 hover:bg-red-50 rounded" data-testid={`delete-contact-${i}`}>
                    <Trash2 size={12} style={{ color: '#D4726A' }} />
                  </button>
                )}
              </div>
              <div className="mt-2 space-y-1">
                {k.phone && <p className="flex items-center gap-1.5 text-xs" style={{ color: '#6B7280' }}><Phone size={11} />{k.phone}</p>}
                {k.email && <p className="flex items-center gap-1.5 text-xs" style={{ color: '#6B7280' }}><Mail size={11} />{k.email}</p>}
                {k.notes && <p className="text-[11px] italic mt-1" style={{ color: '#9CA3AF' }}>{k.notes}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// CONTRACTS — list with expandable terms edit
// ============================================================
function ContractsSection({ dossier, onRefresh }) {
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const client = dossier.client;

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({
      payment_terms: c.payment_terms || '',
      billing_cycle: c.billing_cycle || '',
      scope_of_work: c.scope_of_work || '',
      renewal_clause: c.renewal_clause || '',
      exit_clause: c.exit_clause || '',
      lock_in_period: c.lock_in_period || '',
      start_date: c.start_date || '',
      end_date: c.end_date || '',
      annual_value: c.annual_value || 0,
      contract_value: c.contract_value || 0,
      renewal_status: c.renewal_status || 'active',
      notes: c.notes || '',
    });
  };
  const save = async () => {
    await api.put(`/contracts/${editId}`, form);
    setEditId(null);
    onRefresh();
  };

  const deleteContract = async (c) => {
    const rowCount = (c.payment_schedule || []).length;
    const received = (c.payment_schedule || []).filter(r => r.status === 'received').length;
    const warn = received > 0
      ? `\n\nThis contract has ${received} RECEIVED payment row(s). Deleting it will also remove those from Lifetime Realised.`
      : '';
    if (!window.confirm(`Delete contract "${c.entity || c.client_name}"?\n\nAnnual value ${fC(c.annual_value)} · ${rowCount} payment rows${warn}\n\nThis soft-deletes the contract (recoverable by admin).`)) return;
    try {
      await api.delete(`/data/contracts/${c.id}`);
      onRefresh();
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  if (dossier.contracts.length === 0) {
    return (
      <div className="space-y-3" data-testid="contracts-section">
        <AddContractButton clientName={client.client_name} divisionId={client.division_id} onCreated={onRefresh} />
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No contracts linked to this client yet.</p>
          <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>Click "Add Contract" above to create the first one.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="contracts-section">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Contracts ({dossier.contracts.length})</p>
        <AddContractButton clientName={client.client_name} divisionId={client.division_id} onCreated={onRefresh} />
      </div>
      {dossier.contracts.map(c => {
        const isEditing = editId === c.id;
        return (
          <div key={c.id} className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid={`contract-card-${c.id}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>{c.entity || c.client_name}</p>
                <p className="text-xs" style={{ color: '#6B7280' }}>
                  Annual {fC(c.annual_value)} · {c.start_date || '—'} → {c.end_date || 'Open'} ·
                  <span className="ml-1 capitalize">{c.renewal_status?.replace('_', ' ')}</span>
                </p>
              </div>
              {!isEditing ? (
                <div className="flex items-center gap-3">
                  <button onClick={() => startEdit(c)} className="flex items-center gap-1 text-xs" style={{ color: '#2A7B88' }} data-testid={`edit-contract-${c.id}`}><Pencil size={12} /> Edit Terms</button>
                  <button onClick={() => deleteContract(c)} className="flex items-center gap-1 text-xs" style={{ color: '#D4726A' }} data-testid={`delete-contract-${c.id}`}><Trash2 size={12} /> Delete</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={save} className="px-3 py-1 text-xs font-medium text-white rounded-md" style={{ backgroundColor: '#2A7B88' }} data-testid={`save-contract-${c.id}`}>Save</button>
                  <button onClick={() => setEditId(null)} className="px-3 py-1 text-xs rounded-md border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t" style={{ borderColor: '#F3F4F6' }}>
              {isEditing ? (
                <>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#9CA3AF' }}>Payment Terms (text)</label>
                    <select value={form.payment_terms} onChange={e => setForm(f => ({ ...f, payment_terms: e.target.value }))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                      <option value="">Select...</option>
                      {PAYMENT_TERMS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#9CA3AF' }}>Billing Cycle (text)</label>
                    <select value={form.billing_cycle} onChange={e => setForm(f => ({ ...f, billing_cycle: e.target.value }))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
                      <option value="">Select...</option>
                      {BILLING_CYCLES.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <EditableField label="Scope of Work" value={form.scope_of_work} onChange={v => setForm(f => ({ ...f, scope_of_work: v }))} wide textarea />
                  <EditableField label="Renewal Clause" value={form.renewal_clause} onChange={v => setForm(f => ({ ...f, renewal_clause: v }))} wide />
                  <EditableField label="Exit Clause" value={form.exit_clause} onChange={v => setForm(f => ({ ...f, exit_clause: v }))} />
                  <EditableField label="Lock-in Period" value={form.lock_in_period} onChange={v => setForm(f => ({ ...f, lock_in_period: v }))} placeholder="e.g. 12 months" />
                </>
              ) : (
                <>
                  <RO label="Payment Terms" value={c.payment_terms} />
                  <RO label="Billing Cycle" value={c.billing_cycle} />
                  <RO label="Scope of Work" value={c.scope_of_work} wide />
                  <RO label="Renewal Clause" value={c.renewal_clause} wide />
                  <RO label="Exit Clause" value={c.exit_clause} />
                  <RO label="Lock-in Period" value={c.lock_in_period} />
                </>
              )}
            </div>

            {/* Payment Structure — drives auto-schedule generation */}
            <PaymentStructureEditor contract={c} onSaved={onRefresh} />

            {/* Append-only notes timeline for this contract */}
            <ContractNotes contractId={c.id} notes={c.notes_log || []} onAdded={onRefresh} />
          </div>
        );
      })}
    </div>
  );
}

function RO({ label, value, wide }) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#9CA3AF' }}>{label}</p>
      <p className="text-sm mt-0.5 whitespace-pre-wrap" style={{ color: value ? '#1B2A4A' : '#D4D4D8' }}>{value || '—'}</p>
    </div>
  );
}

function EditableField({ label, value, onChange, wide, placeholder, textarea }) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <label className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#9CA3AF' }}>{label}</label>
      {textarea ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={2} placeholder={placeholder}
          className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC' }} />
      ) : (
        <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
      )}
    </div>
  );
}

// ============================================================
// DOCUMENTS — Cloudinary upload, CEO+Leader gated
// ============================================================
function DocumentsSection({ dossier, onRefresh }) {
  const c = dossier.client;
  const docs = c.documents || [];
  const canEdit = dossier.can_manage_docs;
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ doc_type: 'Contract', label: '', dated: '', notes: '', url: '', public_id: '', original_filename: '' });
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('folder', 'client-documents');
      const r = await api.post('/cloudinary/upload-doc', fd);
      setForm(f => ({
        ...f,
        url: r.data.url,
        public_id: r.data.public_id,
        original_filename: r.data.original_filename || file.name,
      }));
    } catch {
      alert('Upload failed. Check Cloudinary credentials.');
    }
    setUploading(false);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.url) { alert('Please upload a file first'); return; }
    await api.post(`/clients-registry/${c.id}/documents`, form);
    setForm({ doc_type: 'Contract', label: '', dated: '', notes: '', url: '', public_id: '', original_filename: '' });
    setShowForm(false);
    if (fileRef.current) fileRef.current.value = '';
    onRefresh();
  };

  const del = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    await api.delete(`/clients-registry/${c.id}/documents/${id}`);
    onRefresh();
  };

  return (
    <div className="space-y-3" data-testid="documents-section">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Documents ({docs.length})</p>
          {!canEdit && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>Contract documents are confidential — only CEO + Leaders can upload/delete.</p>}
        </div>
        {canEdit && (
          <button onClick={() => setShowForm(s => !s)} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="upload-doc-btn">
            <Upload size={12} /> Upload
          </button>
        )}
      </div>

      {showForm && canEdit && (
        <form onSubmit={save} className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="doc-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] font-medium uppercase" style={{ color: '#9CA3AF' }}>Type *</label>
              <select value={form.doc_type} onChange={e => setForm(f => ({ ...f, doc_type: e.target.value }))} className="w-full px-3 py-2 mt-0.5 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="doc-type-select">
                {(dossier.doc_types || []).map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="Label (e.g. Microsoft MSA FY26)" className="px-3 py-2 rounded-md border text-sm mt-[18px]" style={{ borderColor: '#E5E1DC' }} data-testid="doc-label-input" />
            <input type="date" value={form.dated} onChange={e => setForm(f => ({ ...f, dated: e.target.value }))} className="px-3 py-2 rounded-md border text-sm mt-[18px]" style={{ borderColor: '#E5E1DC' }} />
          </div>
          <div>
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={handleFile} className="text-xs" data-testid="doc-file-input" />
            {uploading && <p className="text-[11px] mt-1" style={{ color: '#2A7B88' }}>Uploading...</p>}
            {form.url && <p className="text-[11px] mt-1 flex items-center gap-1" style={{ color: '#2D8B5E' }}><CheckCircle2 size={11} /> {form.original_filename} ready</p>}
          </div>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Notes (e.g. Supersedes FY25 MSA)" className="w-full px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC' }} />
          <div className="flex gap-2">
            <button type="submit" disabled={!form.url || uploading} className="px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#1B2A4A' }} data-testid="doc-submit">Save Document</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </form>
      )}

      {docs.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No documents yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map(d => (
            <div key={d.id} className="bg-white rounded-xl border p-3 flex items-center gap-3" style={{ borderColor: '#E5E1DC' }} data-testid={`doc-row-${d.id}`}>
              <FileText size={18} style={{ color: '#2A7B88' }} className="flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium truncate" style={{ color: '#1B2A4A' }}>{d.label || d.original_filename}</p>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>{d.doc_type}</span>
                  {d.dated && <span className="text-[10px]" style={{ color: '#9CA3AF' }}>{d.dated}</span>}
                </div>
                {d.notes && <p className="text-[11px] mt-0.5 italic truncate" style={{ color: '#9CA3AF' }}>{d.notes}</p>}
                <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
                  {d.original_filename && `${d.original_filename} · `}by {d.uploaded_by || '—'}
                </p>
              </div>
              <a href={d.url} target="_blank" rel="noreferrer" className="text-[11px] px-2 py-1 rounded bg-blue-50 text-blue-700 flex items-center gap-1" data-testid={`doc-view-${d.id}`}>
                <ExternalLink size={11} /> View
              </a>
              {canEdit && (
                <button onClick={() => del(d.id)} className="p-1 hover:bg-red-50 rounded" data-testid={`doc-delete-${d.id}`}>
                  <Trash2 size={13} style={{ color: '#D4726A' }} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// MEETING NOTES — chronological timeline
// ============================================================
function MeetingsSection({ dossier, onRefresh }) {
  const c = dossier.client;
  const notes = [...(c.meeting_notes || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), attendees: '', summary: '', action_items: '' });

  const save = async (e) => {
    e.preventDefault();
    await api.post(`/clients-registry/${c.id}/meeting-notes`, form);
    setForm({ date: new Date().toISOString().slice(0, 10), attendees: '', summary: '', action_items: '' });
    setShowForm(false);
    onRefresh();
  };
  const del = async (id) => {
    if (!window.confirm('Delete this meeting note?')) return;
    await api.delete(`/clients-registry/${c.id}/meeting-notes/${id}`);
    onRefresh();
  };

  return (
    <div className="space-y-3" data-testid="meetings-section">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Meeting Notes ({notes.length})</p>
        <button onClick={() => setShowForm(s => !s)} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-meeting-btn">
          <Plus size={12} /> Log Meeting
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="bg-white rounded-xl border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="meeting-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input required type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="meeting-date-input" />
            <input value={form.attendees} onChange={e => setForm(f => ({ ...f, attendees: e.target.value }))} placeholder="Attendees (comma-separated)" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
          </div>
          <textarea required value={form.summary} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} rows={3} placeholder="Summary *" className="w-full px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC' }} data-testid="meeting-summary-input" />
          <textarea value={form.action_items} onChange={e => setForm(f => ({ ...f, action_items: e.target.value }))} rows={2} placeholder="Action items / next steps" className="w-full px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC' }} />
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="meeting-submit">Save Note</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </form>
      )}

      {notes.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No meeting notes yet. Log your first interaction.</p>
        </div>
      ) : (
        <div className="relative space-y-3 pl-5 border-l-2" style={{ borderColor: '#E5E1DC' }}>
          {notes.map(n => (
            <div key={n.id} className="relative bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }} data-testid={`meeting-${n.id}`}>
              <div className="absolute -left-[22px] top-3 w-3 h-3 rounded-full border-2" style={{ backgroundColor: '#FFF', borderColor: '#2A7B88' }} />
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs" style={{ color: '#1B2A4A' }}>
                    <Calendar size={11} style={{ color: '#2A7B88' }} />
                    <strong>{n.date}</strong>
                    {n.attendees && <span style={{ color: '#6B7280' }}>· {n.attendees}</span>}
                  </div>
                  <p className="text-sm mt-1.5 whitespace-pre-wrap" style={{ color: '#1B2A4A' }}>{n.summary}</p>
                  {n.action_items && (
                    <div className="mt-2 p-2 rounded" style={{ backgroundColor: '#FFFBEB' }}>
                      <p className="text-[10px] font-semibold" style={{ color: '#92400E' }}>ACTION ITEMS</p>
                      <p className="text-[11px] whitespace-pre-wrap" style={{ color: '#78350F' }}>{n.action_items}</p>
                    </div>
                  )}
                  <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>Logged by {n.logged_by}</p>
                </div>
                <button onClick={() => del(n.id)} className="p-1 hover:bg-red-50 rounded" data-testid={`delete-meeting-${n.id}`}>
                  <Trash2 size={12} style={{ color: '#D4726A' }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// PAYMENTS — mirror of receivables for this client only
// ============================================================
function PaymentsSection({ dossier, onRefresh }) {
  const sch = dossier.schedules || [];
  const t = dossier.schedule_totals || {};
  const variable = (dossier.contracts || []).find(
    (c) => c.payment_structure === 'fixed_plus_variable' && c.renewal_status === 'active'
  );
  const contracts = dossier.contracts || [];
  const primaryContractId = (contracts.find(c => c.renewal_status === 'active') || contracts[0])?.id;
  const [addOverage, setAddOverage] = useState(false);
  const [ovForm, setOvForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    label: 'Variable usage — ',
  });
  const [editKey, setEditKey] = useState(null); // `${contract_id}-${idx}`
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [addNewFor, setAddNewFor] = useState(null); // contract_id currently adding a new row
  const [newForm, setNewForm] = useState({});

  const STATUS_OPTIONS = [
    { v: 'scheduled', label: 'Scheduled' },
    { v: 'pdc', label: 'PDC' },
    { v: 'awaiting_po', label: 'Awaiting PO' },
    { v: 'received', label: 'Received' },
  ];

  const markReceived = async (s) => {
    const today = new Date().toISOString().slice(0, 10);
    if (!window.confirm(`Mark ${fC(s.amount)} as received on ${today}?`)) return;
    try {
      await api.post(`/contracts/${s.contract_id}/payments/${s.idx}/mark-received`, {
        received_date: today, amount: s.amount,
      });
      onRefresh();
    } catch (e) {
      alert('Failed to mark received');
    }
  };

  const startEdit = (s) => {
    setEditKey(`${s.contract_id}-${s.idx}`);
    setEditForm({
      due_date: s.due_date || '',
      amount: s.amount || 0,
      status: s.effective_status === 'overdue' ? 'scheduled' : (s.effective_status || 'scheduled'),
      label: s.label || '',
      notes: s.notes || '',
      invoice_ref: s.invoice_ref || '',
      received_date: s.received_date || '',
    });
  };

  const cancelEdit = () => { setEditKey(null); setEditForm({}); };

  const saveEdit = async (s) => {
    setSaving(true);
    try {
      const body = { ...editForm, amount: parseFloat(editForm.amount) || 0 };
      if (body.status !== 'received') {
        body.received_date = '';
      }
      await api.put(`/contracts/${s.contract_id}/payments/${s.idx}`, body);
      cancelEdit();
      onRefresh();
    } catch (e) {
      alert('Save failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (s) => {
    const warn = s.effective_status === 'received'
      ? `\n\nThis row is RECEIVED — deleting it will also remove ${fC(s.amount)} from Lifetime Realised and the ledger.`
      : '';
    if (!window.confirm(`Delete the ${s.effective_status} row of ${fC(s.amount)} due ${s.due_date || '—'}?${warn}`)) return;
    try {
      await api.delete(`/contracts/${s.contract_id}/payments/${s.idx}`);
      onRefresh();
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const startAddNew = () => {
    if (!primaryContractId) { alert('Add a contract first before creating payment rows.'); return; }
    setAddNewFor(primaryContractId);
    setNewForm({
      due_date: new Date().toISOString().slice(0, 10),
      amount: '',
      status: 'scheduled',
      label: '',
      notes: '',
    });
  };

  const submitNew = async () => {
    const amt = parseFloat(newForm.amount);
    if (!amt || amt <= 0) { alert('Enter a valid amount'); return; }
    setSaving(true);
    try {
      await api.post(`/contracts/${addNewFor}/payments`, { ...newForm, amount: amt });
      setAddNewFor(null);
      setNewForm({});
      onRefresh();
    } catch (e) {
      alert('Add failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const statusColors = {
    received:    { bg: '#ECFDF5', text: '#065F46' },
    overdue:     { bg: '#FEF2F2', text: '#991B1B' },
    pdc:         { bg: '#EFF6FF', text: '#1D4ED8' },
    awaiting_po: { bg: '#FEF3C7', text: '#92400E' },
    scheduled:   { bg: '#F3F4F6', text: '#374151' },
  };

  return (
    <div className="space-y-3" data-testid="payments-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Payment Schedule ({sch.length})</p>
        <div className="flex gap-2">
          <button
            onClick={startAddNew}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md text-white"
            style={{ backgroundColor: '#2A7B88' }}
            data-testid="add-payment-row-btn"
          >
            <Plus size={12} /> Add row
          </button>
          {variable && (
            <button
              onClick={() => setAddOverage(true)}
              className="px-3 py-1.5 text-xs font-medium rounded-md text-white"
              style={{ backgroundColor: '#BA7517' }}
              data-testid="add-overage-btn"
            >
              + Add variable billing
            </button>
          )}
        </div>
      </div>
      {addNewFor && (
        <div className="bg-white border rounded-xl p-4 space-y-2" style={{ borderColor: '#2A7B88' }} data-testid="new-row-form">
          <p className="text-xs font-semibold" style={{ color: '#2A7B88' }}>New payment row</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-sm">
            <div>
              <label className="text-[10px] uppercase text-[#6B7280]">Due date</label>
              <input type="date" value={newForm.due_date || ''} onChange={e => setNewForm({ ...newForm, due_date: e.target.value })}
                className="w-full px-2 py-1.5 border rounded text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="new-row-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[#6B7280]">Amount (₹)</label>
              <input type="number" value={newForm.amount || ''} onChange={e => setNewForm({ ...newForm, amount: e.target.value })}
                className="w-full px-2 py-1.5 border rounded text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="new-row-amount" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[#6B7280]">Status</label>
              <select value={newForm.status || 'scheduled'} onChange={e => setNewForm({ ...newForm, status: e.target.value })}
                className="w-full px-2 py-1.5 border rounded text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="new-row-status">
                {STATUS_OPTIONS.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase text-[#6B7280]">Label</label>
              <input value={newForm.label || ''} onChange={e => setNewForm({ ...newForm, label: e.target.value })}
                placeholder="e.g. Q3 FY26 instalment"
                className="w-full px-2 py-1.5 border rounded text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="new-row-label" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setAddNewFor(null)} className="text-xs text-[#6B7280] px-3 py-1.5">Cancel</button>
            <button onClick={submitNew} disabled={saving}
              className="text-xs px-4 py-1.5 rounded-md text-white disabled:opacity-50"
              style={{ backgroundColor: '#2D8B5E' }} data-testid="new-row-submit">
              {saving ? 'Saving…' : 'Save row'}
            </button>
          </div>
        </div>
      )}
      {variable && addOverage && (
        <div className="bg-white border border-[#BA7517] rounded-xl p-4 space-y-2" data-testid="overage-form">
          <p className="text-xs font-semibold text-[#BA7517]">
            Log variable billing on top of MG floor ({fC(variable.annual_value)})
          </p>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>
              <label className="text-[10px] uppercase text-[#6B7280]">Date</label>
              <input type="date" value={ovForm.date} onChange={(e) => setOvForm({ ...ovForm, date: e.target.value })}
                className="w-full px-2 py-1.5 border border-[#E5E1DC] rounded text-sm" data-testid="overage-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[#6B7280]">Amount (₹)</label>
              <input type="number" value={ovForm.amount} onChange={(e) => setOvForm({ ...ovForm, amount: e.target.value })}
                placeholder="e.g. 1200000" className="w-full px-2 py-1.5 border border-[#E5E1DC] rounded text-sm" data-testid="overage-amount" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-[#6B7280]">Label</label>
              <input value={ovForm.label} onChange={(e) => setOvForm({ ...ovForm, label: e.target.value })}
                className="w-full px-2 py-1.5 border border-[#E5E1DC] rounded text-sm" data-testid="overage-label" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setAddOverage(false)} className="text-xs text-[#6B7280] px-3 py-1.5">Cancel</button>
            <button
              onClick={async () => {
                if (!ovForm.amount || Number(ovForm.amount) <= 0) { alert('Enter an amount'); return; }
                try {
                  await api.post(`/contracts/${variable.id}/overage`, {
                    date: ovForm.date,
                    amount: Number(ovForm.amount),
                    label: ovForm.label || 'Variable overage',
                  });
                  setAddOverage(false);
                  setOvForm({ date: new Date().toISOString().slice(0, 10), amount: '', label: 'Variable usage — ' });
                  onRefresh();
                } catch (e) {
                  alert('Failed: ' + (e?.response?.data?.detail || e.message));
                }
              }}
              className="text-xs px-4 py-1.5 rounded-md text-white"
              style={{ backgroundColor: '#2D8B5E' }}
              data-testid="overage-submit"
            >
              Add billing
            </button>
          </div>
          <p className="text-[10px] text-[#6B7280]">
            This appends a new payment row on top of the MG floor. Billed-to-date
            will grow past {fC(variable.annual_value)} as variable usage is invoiced.
          </p>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <SumCard label="Received" value={t.received || 0} color="#2D8B5E" />
        <SumCard label="Overdue" value={t.overdue || 0} color="#DC2626" />
        <SumCard label="Scheduled" value={t.scheduled || 0} color="#6B7280" />
        <SumCard label="PDC" value={t.pdc || 0} color="#1D4ED8" />
        <SumCard label="Awaiting PO" value={t.awaiting_po || 0} color="#D97706" />
      </div>
      {sch.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No payment schedule yet.</p>
          <p className="text-[11px] mt-1" style={{ color: '#9CA3AF' }}>Click "Add row" above or configure a billing cycle on the contract to auto-generate.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
          <table className="w-full text-sm">
            <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
              <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Due Date</th>
              <th className="text-left px-3 py-2 text-xs font-medium hidden sm:table-cell" style={{ color: '#6B7280' }}>Label / Entity</th>
              <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Amount</th>
              <th className="text-center px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Status</th>
              <th className="text-right px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Actions</th>
            </tr></thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {sch.map((s, i) => {
                const key = `${s.contract_id}-${s.idx}`;
                const isEditing = editKey === key;
                const sc = statusColors[s.effective_status] || statusColors.scheduled;
                if (isEditing) {
                  return (
                    <tr key={i} style={{ backgroundColor: '#F0FDF4' }} data-testid={`schedule-row-${i}-edit`}>
                      <td className="px-3 py-2">
                        <input type="date" value={editForm.due_date} onChange={e => setEditForm({ ...editForm, due_date: e.target.value })}
                          className="w-36 px-1.5 py-1 border rounded text-xs" style={{ borderColor: '#E5E1DC' }} data-testid={`edit-due-${i}`} />
                      </td>
                      <td className="px-3 py-2 hidden sm:table-cell">
                        <input value={editForm.label || ''} onChange={e => setEditForm({ ...editForm, label: e.target.value })}
                          placeholder="Label / notes" className="w-full px-1.5 py-1 border rounded text-xs" style={{ borderColor: '#E5E1DC' }} data-testid={`edit-label-${i}`} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" value={editForm.amount} onChange={e => setEditForm({ ...editForm, amount: e.target.value })}
                          className="w-28 px-1.5 py-1 border rounded text-xs text-right" style={{ borderColor: '#E5E1DC' }} data-testid={`edit-amount-${i}`} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                          className="px-1.5 py-1 border rounded text-xs" style={{ borderColor: '#E5E1DC' }} data-testid={`edit-status-${i}`}>
                          {STATUS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
                        </select>
                        {editForm.status === 'received' && (
                          <input type="date" value={editForm.received_date || ''} onChange={e => setEditForm({ ...editForm, received_date: e.target.value })}
                            className="mt-1 w-32 px-1.5 py-1 border rounded text-[10px]" style={{ borderColor: '#E5E1DC' }} data-testid={`edit-received-${i}`} />
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => saveEdit(s)} disabled={saving}
                          className="text-[11px] px-2 py-1 rounded text-white font-medium mr-1 disabled:opacity-50"
                          style={{ backgroundColor: '#2A7B88' }} data-testid={`save-edit-${i}`}>
                          {saving ? '…' : 'Save'}
                        </button>
                        <button onClick={cancelEdit} className="text-[11px] px-2 py-1 rounded border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid={`cancel-edit-${i}`}>
                          Cancel
                        </button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={i} data-testid={`schedule-row-${i}`}>
                    <td className="px-3 py-2" style={{ color: '#1B2A4A' }}>{s.due_date || '—'}</td>
                    <td className="px-3 py-2 text-xs hidden sm:table-cell" style={{ color: '#6B7280' }}>
                      {s.label || s.notes || s.contract_entity}
                    </td>
                    <td className="px-3 py-2 text-right font-medium" style={{ color: '#1B2A4A' }}>{fC(s.amount)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize" style={{ backgroundColor: sc.bg, color: sc.text }}>
                        {s.effective_status.replace('_', ' ')}
                      </span>
                      {s.effective_status === 'received' && s.received_date && (
                        <div className="text-[9px] mt-0.5" style={{ color: '#9CA3AF' }}>{s.received_date}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {s.effective_status !== 'received' && (
                        <button onClick={() => markReceived(s)}
                          className="text-[11px] px-2 py-1 rounded bg-green-50 hover:bg-green-100 font-medium mr-1"
                          style={{ color: '#065F46' }} data-testid={`mark-received-${i}`}>
                          Mark Received
                        </button>
                      )}
                      <button onClick={() => startEdit(s)}
                        className="p-1 rounded hover:bg-slate-100 mr-0.5"
                        title="Edit row" data-testid={`edit-row-${i}`}>
                        <Pencil size={12} style={{ color: '#2A7B88' }} />
                      </button>
                      <button onClick={() => deleteRow(s)}
                        className="p-1 rounded hover:bg-red-50"
                        title="Delete row" data-testid={`delete-row-${i}`}>
                        <Trash2 size={12} style={{ color: '#D4726A' }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SumCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-lg border p-2.5" style={{ borderColor: '#E5E1DC' }}>
      <p className="text-[9px] uppercase tracking-wide" style={{ color: '#9CA3AF' }}>{label}</p>
      <p className="text-sm font-bold" style={{ color }}>{fC(value)}</p>
    </div>
  );
}

// ============================================================
// HISTORY — lifetime revenue by FY
// ============================================================
function HistorySection({ dossier }) {
  const hist = dossier.revenue_history || [];
  const max = Math.max(1, ...hist.map(h => h.amount));
  return (
    <div className="space-y-3" data-testid="history-section">
      <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Lifetime Revenue</p>
      <div className="bg-white rounded-xl border p-4" style={{ borderColor: '#E5E1DC' }}>
        <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: '#9CA3AF' }}>Total realised since inception</p>
        <p className="text-2xl font-bold" style={{ color: '#2D8B5E' }}>{fC(dossier.total_realised_lifetime)}</p>
      </div>
      {hist.length === 0 ? (
        <div className="text-center py-10 bg-white rounded-xl border" style={{ borderColor: '#E5E1DC' }}>
          <p className="text-sm" style={{ color: '#9CA3AF' }}>No realised revenue recorded yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border p-4 space-y-2" style={{ borderColor: '#E5E1DC' }}>
          {hist.map(h => (
            <div key={h.label} className="flex items-center gap-3">
              <span className="text-xs font-medium w-20" style={{ color: '#6B7280' }}>{h.label}</span>
              <div className="flex-1 h-3 rounded-full bg-[#F7F5F2] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${(h.amount / max) * 100}%`, backgroundColor: '#2A7B88' }} />
              </div>
              <span className="text-xs font-medium w-20 text-right" style={{ color: '#1B2A4A' }}>{fC(h.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
